// src/services/useCompaniesService.ts
import { useCallback, useState } from 'react';
import useFetch, { ServiceParams, ServiceResponse } from '../hooks/useFetch';
import EndPoints from '../config/EndPoint';

export type Location = { id?: number; name?: string; created_at?: string; is_active?: boolean };
export type Events = Partial<{ onSuccess:(r:ServiceResponse)=>void; onError:(r:ServiceResponse)=>void }>;

const qs = (filters?: Record<string,any>) => {
  if (!filters) return '';
  return Object.entries(filters)
    .filter(([,v]) => v !== undefined && v !== null && v !== '')
    .map(([k,v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
};

/**
 * useCompaniesService
 * - Funciones memoizadas para evitar que cambien identidad entre renders.
 * - `fetching` sigue siendo estado y puede cambiar sin recrear funciones.
 */
const useLocationsService = () => {
  const { doGet, doPost, doPut, doDelete } = useFetch();
  const [fetching, setFetching] = useState<Record<string,boolean>>({});

  // setFetch memoizado
  const setFetch = useCallback((key: string, val: boolean) => {
    setFetching(prev => ({ ...prev, [key]: val }));
  }, []);

  const list = useCallback((filters?: Record<string,any>, events: Events = {}, signal?: AbortSignal) =>
    doGet({
      url: `${EndPoints.LOCATIONS.LIST}${qs(filters) ? `?${qs(filters)}` : ''}`,
      setFetching: v => setFetch('list', v),
      signal,
      ...events
    } as ServiceParams)
  , [doGet, setFetch]);

  const show = useCallback((id: number, events: Events = {}) =>
    doGet({
      url: EndPoints.LOCATIONS.SHOW.replace(':id', String(id)),
      setFetching: v => setFetch('show', v),
      ...events
    } as ServiceParams)
  , [doGet, setFetch]);

  const store = useCallback((payload: { name: string }, events: Events = {}) =>
    doPost({
      url: EndPoints.LOCATIONS.STORE,
      body: payload,
      setFetching: v => setFetch('store', v),
      ...events
    } as ServiceParams)
  , [doPost, setFetch]);

  const update = useCallback((id: number, payload: { name: string }, events: Events = {}) =>
    doPut({
      url: EndPoints.LOCATIONS.UPDATE.replace(':id', String(id)),
      body: payload,
      setFetching: v => setFetch('update', v),
      ...events
    } as ServiceParams)
  , [doPut, setFetch]);

  const remove = useCallback((id: number, events: Events = {}) =>
    doDelete({
      url: EndPoints.LOCATIONS.DELETE.replace(':id', String(id)),
      setFetching: v => setFetch('delete', v),
      ...events
    } as ServiceParams)
  , [doDelete, setFetch]);

  return { fetching, list, show, store, update, remove };
};

export default useLocationsService;
